import { useParams, useOutletContext, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath, canonicalSlug } from "@/lib/routes";
import { toLangSlug } from "@/lib/slug_map";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { serviceSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";
import { SERVICE_ITEMS } from "@/lib/nav";
import { ContentBlocks } from "@/components/ContentBlocks";

export default function ServiceDetail() {
  const { slug: urlSlug } = useParams();
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  // Resolve any incoming slug (DA or EN) to the canonical DA slug used for lookup.
  const daSlug = canonicalSlug("service", urlSlug);
  const { data: service, isLoading } = useQuery({ queryKey: ["service", daSlug], queryFn: () => api.getService(daSlug) });

  // If the URL is not the canonical form for the current language, redirect.
  // e.g. /en/services/teknisk-seo → /en/services/technical-seo
  const canonicalUrlSlug = toLangSlug("service", daSlug, lang);
  if (canonicalUrlSlug && urlSlug !== canonicalUrlSlug) {
    return <Navigate to={buildPath("service", lang, { slug: daSlug })} replace />;
  }

  if (isLoading || !service) return <div className="px-6 md:px-20 py-32 text-muted-foreground">…</div>;
  const c = service[lang] || service.da;
  const ex = service.extra?.[lang];
  const path = buildPath("service", lang, { slug: daSlug });
  const others = SERVICE_ITEMS.filter((x) => x.slug !== daSlug).slice(0, 3);

  return (
    <>
      <Seo routeKey="service" params={{ slug: daSlug }} lang={lang} title={c.meta_title} description={c.meta_description}
        jsonLd={[
          serviceSchema(service, lang, path),
          faqSchema(c.faq || []),
          breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_services"), path: buildPath("services", lang) }, { name: c.title, path }]),
        ]} />
      <PageHero title={c.title} description={c.tagline}
        crumbs={[{ label: t("nav_home"), to: "home" }, { label: t("nav_services"), to: "services" }, { label: c.title }]} />

      <section className="px-6 md:px-12 lg:px-20 py-16">
        <div className="grid gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-14">
            <Reveal>
              <p className="text-xl font-light leading-relaxed text-foreground/90">{c.intro}</p>
            </Reveal>
            {(c.sections || []).map((sec, i) => (
              <Reveal key={i} delay={0.05}>
                <div className="border-t border-border pt-8">
                  <span className="font-mono text-xs text-primary">0{i + 1}</span>
                  <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold tracking-tight">{sec.h}</h2>
                  <p className="mt-4 text-muted-foreground font-light leading-relaxed">{sec.p}</p>
                </div>
              </Reveal>
            ))}
            {ex?.paras?.length > 0 && (
              <Reveal>
                <div className="space-y-4 border-t border-border pt-8">
                  {ex.paras.map((p, i) => (
                    <p key={i} className="text-muted-foreground font-light leading-relaxed">{p}</p>
                  ))}
                </div>
              </Reveal>
            )}
            {ex?.blocks?.length > 0 && (
              <div className="border-t border-border pt-8">
                <ContentBlocks blocks={ex.blocks} lang={lang} />
              </div>
            )}
            {ex?.process?.length > 0 && (
              <Reveal>
                <div className="border-t border-border pt-8">
                  <h2 className="mb-6 font-heading text-2xl md:text-3xl font-bold tracking-tight">
                    {lang === "da" ? "Sådan arbejder jeg" : "How I work"}
                  </h2>
                  <div className="space-y-5">
                    {ex.process.map((s, i) => (
                      <div key={i} className="flex gap-4">
                        <span className="shrink-0 font-mono text-sm text-muted-foreground">0{i + 1}</span>
                        <div>
                          <h3 className="font-heading font-bold">{s.h}</h3>
                          <p className="mt-1 text-sm text-muted-foreground font-light leading-relaxed">{s.p}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 rounded-xl border border-border bg-card p-7">
              <div className="label-mono text-primary">{t("what_included")}</div>
              <ul className="mt-5 space-y-3">
                {(c.deliverables || []).map((d, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <Check size={18} weight="bold" className="mt-0.5 shrink-0 text-primary" />
                    <span className="text-muted-foreground">{d}</span>
                  </li>
                ))}
              </ul>
              <Link to={buildPath("contact", lang)} data-testid="service-cta"
                className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3.5 font-medium text-primary-foreground transition-transform hover:scale-[1.03]">
                {c.cta}
                <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {ex?.table && (
        <section className="border-t border-border px-6 md:px-12 lg:px-20 py-16">
          <h2 className="mb-6 font-heading text-2xl md:text-3xl font-bold tracking-tight">{ex.table.title}</h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-left text-sm" data-testid="service-table">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  {ex.table.head.map((h, i) => (
                    <th key={i} className="px-5 py-3 font-heading font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ex.table.rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-border last:border-0">
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-5 py-3 ${ci === 0 ? "font-medium" : "text-muted-foreground"}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {c.faq?.length > 0 && (
        <section className="border-t border-border px-6 md:px-12 lg:px-20 py-16">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">{t("faq")}</h2>
          <Accordion type="single" collapsible className="mt-8 max-w-3xl">
            {c.faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-heading text-lg font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-light leading-relaxed">
                  {String(f.a).split(/\n\n+/).map((para, pi) => (
                    <p key={pi} className={pi > 0 ? "mt-3" : ""}>{para}</p>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* Related services */}
      <section className="border-t border-border px-6 md:px-12 lg:px-20 py-16">
        <div className="label-mono mb-6 text-muted-foreground/60">{t("related_services")}</div>
        <div className="grid gap-5 md:grid-cols-3">
          {others.map((o) => (
            <Link key={o.slug} to={buildPath("service", lang, { slug: o.slug })}
              className="card-hover group flex items-center justify-between rounded-xl border border-border bg-card p-6">
              <span className="font-heading text-lg font-bold">{o[lang]}</span>
              <ArrowRight size={18} className="text-muted-foreground group-hover:text-primary" />
            </Link>
          ))}
        </div>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
